import BaseModel from '@/data/models/base/base-model';
import TypeUtils from '@/helpers/type-utils';
import DateUtils from '@/helpers/date-utils';

type Grow<T, A extends Array<T>> = ((x: T, ...xs: A) => void) extends ((...a: infer X) => void) ? X : never;
type GrowToSize<T, A extends Array<T>, N extends number> = { 0: A, 1: GrowToSize<T, Grow<T, A>, N> }[A['length'] extends N ? 0 : 1];
export type FixedArray<T, N extends number> = GrowToSize<T, [], N>;

export type weekPlanningType = FixedArray<{ hours: (FixedArray<{ state: boolean }, 24>) }, 7>

export const fillWithEmptyPlanning = (): weekPlanningType => {
  return new Array(7).fill(false).map(() => {
    return {hours: new Array(24).fill(false).map((value) => ({'state': value}))};
  }) as weekPlanningType;
};

export default class Building extends BaseModel {

  clientId: string;
  name: string;
  isolation: number;
  surface: number;
  buildDate: Date;
  occupancyCount: number = 1;
  dayTemp: number = 22;
  nightTemp: number = 18;
  occupancyPlanning: weekPlanningType = fillWithEmptyPlanning();
  heatingPlanning: weekPlanningType = fillWithEmptyPlanning();
  coolingPlanning: weekPlanningType = fillWithEmptyPlanning();
  ventilationPlanning: weekPlanningType = fillWithEmptyPlanning();

  dayFreshFlowRate: number = 0;
  nightFreshFlowRate: number = 0;
  dayRecovEff: number = 0;
  nightRecovEff: number = 0;
  lattitude: number = 0;

  coolingDayTemp: number = 22;
  coolingNightTemp: number = 18;

  importSettings: string = '{}';

  get buildDateLocalized(): string {
    return DateUtils.toDDMMYYYY(this.buildDate);
  }

  static map(data: Partial<Building>): Building | null {
    if (!data) {
      return null;
    }

    const building = new Building();

    building.id = data.id;
    building.clientId = TypeUtils.hasStringOrDefault(data.clientId, null);
    building.name = TypeUtils.hasStringOrDefault(data.name, '');
    building.isolation = TypeUtils.hasNumberOrDefault(data.isolation, 0);
    building.surface = TypeUtils.hasNumberOrDefault(data.surface, 0);
    building.buildDate = TypeUtils.hasDateOrDefault(data.buildDate);

    building.occupancyPlanning = TypeUtils.hasArrayOrDefault(data.occupancyPlanning, fillWithEmptyPlanning()) as weekPlanningType;
    building.heatingPlanning = TypeUtils.hasArrayOrDefault(data.heatingPlanning, fillWithEmptyPlanning()) as weekPlanningType;
    building.coolingPlanning = TypeUtils.hasArrayOrDefault(data.coolingPlanning, fillWithEmptyPlanning()) as weekPlanningType;
    building.ventilationPlanning = TypeUtils.hasArrayOrDefault(data.ventilationPlanning, fillWithEmptyPlanning()) as weekPlanningType;

    // Building settings
    building.occupancyCount = TypeUtils.hasNumberOrDefault(data.occupancyCount, 1);
    building.dayTemp = TypeUtils.hasNumberOrDefault(data.dayTemp, 22);
    building.nightTemp = TypeUtils.hasNumberOrDefault(data.nightTemp, 18);
    building.coolingDayTemp = TypeUtils.hasNumberOrDefault(data.coolingDayTemp, 22);
    building.coolingNightTemp = TypeUtils.hasNumberOrDefault(data.coolingNightTemp, 18);

    building.dayFreshFlowRate = TypeUtils.hasNumberOrDefault(data.dayFreshFlowRate, 0);
    building.nightFreshFlowRate = TypeUtils.hasNumberOrDefault(data.nightFreshFlowRate, 0);
    building.dayRecovEff = TypeUtils.hasNumberOrDefault(data.dayRecovEff, 0);
    building.nightRecovEff = TypeUtils.hasNumberOrDefault(data.nightRecovEff, 0);
    building.lattitude = TypeUtils.hasNumberOrDefault(data.lattitude, 0);

    if(typeof data.importSettings === 'object') {
      building.importSettings = JSON.stringify(data.importSettings);
    } else {
      building.importSettings = TypeUtils.hasStringOrDefault(data.importSettings, '{}');

      // Pretty print the JSON
      try {
        building.importSettings = JSON.stringify(JSON.parse(building.importSettings), null, 2);
      } catch (e) {
        building.importSettings = '{}';
      }
    }

    return building;
  }

  toJSON(): Partial<Building> {
    return {
      'clientId': this.clientId,
      'name': this.name,
      'isolation': this.isolation,
      'surface': this.surface,
      'buildDate': this.buildDate,
      'occupancyPlanning': this.occupancyPlanning,
      'heatingPlanning': this.heatingPlanning,
      'coolingPlanning': this.coolingPlanning,
      'ventilationPlanning': this.ventilationPlanning,
      'occupancyCount': this.occupancyCount,
      'dayTemp': this.dayTemp,
      'nightTemp': this.nightTemp,
      'coolingDayTemp': this.coolingDayTemp,
      'coolingNightTemp': this.coolingNightTemp,
      'dayFreshFlowRate': this.dayFreshFlowRate,
      'nightFreshFlowRate': this.nightFreshFlowRate,
      'dayRecovEff': this.dayRecovEff,
      'nightRecovEff': this.nightRecovEff,
      'lattitude': this.lattitude,
      'importSettings': JSON.stringify(JSON.parse(this.importSettings)) || '{}',
    };
  }

  datePickerToBuildDate(date: string): void {
    this.buildDate = DateUtils.isoStringToDate(date);
  }

}
